import { t } from "i18next";
import { TickCircle } from "iconsax-react";
import { useContext, useEffect, useRef, useState } from "react";
import DBButton from "../../component/global/DBButton";
import DBFieldBlock from "../../component/global/DBFieldBlock";
import DBInput from "../../component/global/DBInput";
import DBSelect from "../../component/global/DBSelect";
import { post } from "../../controlers/global/ConfigRequest";
import { parseFeatureOptions } from "../../feedback/helperFunctions";
import { LOGIN_ADDRESS, SEND_OTP } from "../../helperData/ApiAddress";
import {
    applyTokenResponse,
    getLoginErrorMessage,
} from "../../helperData/panelRoutes";
import { GlobalStateContext } from "../../layouts/GlobalStates";
import { normalizeFaNumbers } from "../../utils/methods";

const OTP_LENGTH = 5;

const toPhoneStr = (phone) => {
    const value = normalizeFaNumbers(phone || "").replace(/[\s-]/g, "");
    if (!value) return "";
    if (value[0] === "+") return value;
    if (value[0] === "0" && value[1] === "9" && value.length === 11) {
        return "+98" + value.slice(1);
    }
    return "";
};

export default function SurveyIdentityPanel({
    features = [],
    featureValues,
    onFeatureChange,
    featureErrors = {},
    onBack,
    onSubmit,
    submitLoading,
}) {
    const { setAuth, setRefreshNewCount, setCurrentPanel } =
        useContext(GlobalStateContext);
    const [loggedIn, setLoggedIn] = useState(
        Boolean(sessionStorage.getItem("auth"))
    );
    const [phone, setPhone] = useState("");
    const [code, setCode] = useState("*".repeat(OTP_LENGTH));
    const [codeSent, setCodeSent] = useState(false);
    const [phoneVerified, setPhoneVerified] = useState(false);
    const [otpTimer, setOtpTimer] = useState(0);
    const [authError, setAuthError] = useState("");
    const [authLoading, setAuthLoading] = useState(false);
    const otpRefs = useRef([]);
    const verifyingRef = useRef(false);

    useEffect(() => {
        if (otpTimer <= 0) return undefined;
        const timer = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
        return () => clearTimeout(timer);
    }, [otpTimer]);

    const finishAuth = (tokenResponse) => {
        applyTokenResponse(setAuth, tokenResponse);
        setRefreshNewCount((prev) => !prev);
        const panel =
            Number(tokenResponse.user.user_level) <= 2 ? "organ" : "user";
        setCurrentPanel(panel);
        setLoggedIn(true);
        setPhoneVerified(true);
        setAuthError("");
    };

    const verifyOtp = (otp) => {
        const phoneStr = toPhoneStr(phone);
        if (
            verifyingRef.current ||
            phoneVerified ||
            !phoneStr ||
            otp.length !== OTP_LENGTH
        ) {
            return;
        }
        verifyingRef.current = true;
        setAuthLoading(true);
        setAuthError("");
        post(LOGIN_ADDRESS, {
            username: phoneStr,
            password: otp,
            grant_type: "password",
        })
            .then((response) => finishAuth(response))
            .catch((error) => {
                setAuthError(
                    getLoginErrorMessage(
                        error,
                        t("survey.response.identity.otp.invalid")
                    )
                );
            })
            .finally(() => {
                verifyingRef.current = false;
                setAuthLoading(false);
            });
    };

    const handleSendOtp = () => {
        const phoneStr = toPhoneStr(phone);
        if (!phoneStr) {
            setAuthError(
                t("Src.Component.Global.AuthModal.Login.setPhoneErr")
            );
            return;
        }
        setAuthError("");
        setAuthLoading(true);
        setOtpTimer(120);
        post(SEND_OTP, {
            phone_number: phoneStr,
            create_if_missing: true,
        })
            .then(() => {
                setCodeSent(true);
                setCode("*".repeat(OTP_LENGTH));
                setTimeout(() => otpRefs.current[0]?.focus(), 0);
            })
            .catch(() => {
                setOtpTimer(0);
                setAuthError(
                    t("Src.Component.Global.AuthModal.Login.setResultCodeErr")
                );
            })
            .finally(() => setAuthLoading(false));
    };

    const applyCode = (nextCode) => {
        setCode(nextCode);
        const otp = nextCode.replace(/\*/g, "");
        if (otp.length === OTP_LENGTH && !nextCode.includes("*")) {
            verifyOtp(otp);
        }
    };

    const codeValueHandle = (event, index) => {
        const raw = normalizeFaNumbers(event.target.value).replace(/\D/g, "");
        if (raw.length > 1) {
            const next = [..."*".repeat(OTP_LENGTH)];
            raw.slice(0, OTP_LENGTH).split("").forEach((digit, offset) => {
                next[offset] = digit;
            });
            applyCode(next.join(""));
            const focusAt = Math.min(raw.length, OTP_LENGTH) - 1;
            otpRefs.current[focusAt]?.focus();
            return;
        }
        const next = [...code];
        next[index] = raw.length === 1 ? raw : "*";
        applyCode(next.join(""));
        if (raw.length === 1 && index < OTP_LENGTH - 1) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    const handleCodeKeyDown = (event, index) => {
        if (event.key === "Backspace" && (code[index] === "*" || !code[index])) {
            const previous = index - 1;
            if (previous >= 0) {
                const next = [...code];
                next[previous] = "*";
                applyCode(next.join(""));
                otpRefs.current[previous]?.focus();
            }
        }
    };

    const renderFeatureField = (feature) => {
        const value = featureValues[feature.id] || "";
        const error = featureErrors[feature.id];
        const type = feature.feature_type;
        if (type === 5) {
            return (
                <DBFieldBlock
                    key={feature.id}
                    title={feature.name}
                    required={feature.mandatory}
                    error={error}
                >
                    <DBSelect
                        size="large"
                        allowClear
                        status={error && "error"}
                        placeholder={t("form.select")}
                        value={value || undefined}
                        onChange={(next) =>
                            onFeatureChange(feature.id, next || "")
                        }
                        options={parseFeatureOptions(feature.feature_options)}
                    />
                </DBFieldBlock>
            );
        }
        return (
            <DBFieldBlock
                key={feature.id}
                title={feature.name}
                required={feature.mandatory}
                error={error}
            >
                <DBInput
                    size="large"
                    status={error && "error"}
                    type={type === 4 ? "tel" : "text"}
                    value={value}
                    onChange={(event) =>
                        onFeatureChange(feature.id, event.target.value)
                    }
                />
            </DBFieldBlock>
        );
    };

    return (
        <div className="survey-identity-panel">
            <h2>{t("survey.response.identity.title")}</h2>
            <p>{t("survey.response.identity.lead")}</p>
            {loggedIn && !phoneVerified ? (
                <p className="survey-identity-logged-in">
                    {t("survey.response.identity.logged.in")}
                </p>
            ) : (
                <div className="survey-identity-auth-grid">
                    <div className="survey-identity-phone-row">
                        <DBFieldBlock title={t("survey.response.identity.phone")}>
                            <div className="survey-identity-phone-control">
                                <DBInput
                                    size="large"
                                    type="tel"
                                    value={phone}
                                    disabled={phoneVerified}
                                    onChange={(event) => {
                                        setPhone(event.target.value);
                                        setCodeSent(false);
                                        setPhoneVerified(false);
                                        setCode("*".repeat(OTP_LENGTH));
                                        setAuthError("");
                                    }}
                                />
                                {phoneVerified && (
                                    <TickCircle
                                        className="survey-identity-phone-check"
                                        size={22}
                                        variant="Bold"
                                        color="#059669"
                                        aria-label={t(
                                            "survey.response.identity.otp.verified"
                                        )}
                                    />
                                )}
                            </div>
                        </DBFieldBlock>
                        {!phoneVerified && (
                            <DBButton
                                type="secondary"
                                className="survey-identity-send-code"
                                caption={
                                    otpTimer > 0
                                        ? `${otpTimer}`
                                        : t("survey.response.identity.otp.send")
                                }
                                disabled={otpTimer > 0 || authLoading}
                                loading={authLoading && !codeSent}
                                onClick={handleSendOtp}
                            />
                        )}
                    </div>
                    {codeSent && !phoneVerified && (
                        <div className="forget-numbers" dir="ltr">
                            {Array.from({ length: OTP_LENGTH }, (_, index) => (
                                <input
                                    key={index}
                                    ref={(node) => {
                                        otpRefs.current[index] = node;
                                    }}
                                    type="tel"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={index === 0 ? OTP_LENGTH : 1}
                                    value={
                                        code[index] === "*" ? "" : code[index]
                                    }
                                    disabled={authLoading}
                                    aria-label={t(
                                        "survey.response.identity.otp.send"
                                    )}
                                    onChange={(event) =>
                                        codeValueHandle(event, index)
                                    }
                                    onKeyDown={(event) =>
                                        handleCodeKeyDown(event, index)
                                    }
                                />
                            ))}
                        </div>
                    )}
                    {authError && (
                        <p className="survey-response-error" role="alert">
                            {authError}
                        </p>
                    )}
                </div>
            )}
            {features.length > 0 && (
                <div className="survey-identity-features">
                    <h3>{t("survey.response.identity.fields")}</h3>
                    {features.map(renderFeatureField)}
                </div>
            )}
            <div className="survey-step-nav">
                <DBButton
                    type="primary"
                    outlined
                    className="survey-step-nav-btn"
                    caption={t("prev.label")}
                    onClick={onBack}
                />
                <DBButton
                    className="survey-step-nav-btn"
                    caption={t("survey.response.submit")}
                    loading={submitLoading}
                    disabled={!loggedIn}
                    onClick={onSubmit}
                />
            </div>
        </div>
    );
}
